/**
 * @fileoverview Unlock Dialog Context
 *
 * Provides global state management for the WebAuthn unlock dialog.
 * Allows any component (like UnlockStatusButton) to trigger the unlock dialog.
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

import { createLogger } from '@xanectech/lib';
const logger = createLogger('TrueID.Locker - UnlockDialogContext');

interface UnlockDialogContextType {
  isOpen: boolean;
  openDialog: () => void;
  closeDialog: () => void;
}

const UnlockDialogContext = createContext<UnlockDialogContextType | undefined>(
  undefined,
);

interface UnlockDialogProviderProps {
  children: React.ReactNode;
}

export const UnlockDialogProvider: React.FC<UnlockDialogProviderProps> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const openDialog = useCallback(() => {
    logger.info('UnlockDialogContext: Opening unlock dialog');
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    logger.info('UnlockDialogContext: Closing unlock dialog');
    setIsOpen(false);
  }, []);

  return (
    <UnlockDialogContext.Provider value={{ isOpen, openDialog, closeDialog }}>
      {children}
    </UnlockDialogContext.Provider>
  );
};

export const useUnlockDialog = (): UnlockDialogContextType => {
  const context = useContext(UnlockDialogContext);
  if (!context) {
    throw new Error('useUnlockDialog must be used within UnlockDialogProvider');
  }
  return context;
};
